import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import moment from 'moment';

// material-ui
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// icons
import { CloseOutlined, EditOutlined, FormOutlined, TrophyOutlined } from '@ant-design/icons';

// project import
import RedesignShell from 'components/redesign/RedesignShell';
import HeaderUtilities from 'components/redesign/HeaderUtilities';
import BrokerConnectionStatus from 'components/redesign/BrokerConnectionStatus';
import Profile from 'layout/Dashboard/Header/HeaderContent/Profile';
import useAuthenticatedRequest from 'hooks/useAuthenticatedRequest';
import useIbkrBrokerLiveState from 'hooks/useIbkrBrokerLiveState';
import { openSnackbar } from 'api/snackbar';
import countries from 'data/countries';

const AVATAR_GRADIENT = 'linear-gradient(135deg,#c9d3e0,#aab6c6)';
const EXPERIENCE_OPTIONS = ['beginner', 'intermediate', 'advanced', 'expert'];
const cardSx = { bgcolor: 'var(--app-surface)', border: '1px solid var(--app-border)', borderRadius: '18px', p: { xs: 2.5, md: 3 } };

// Normalise a backend list (of strings or {id,name}) into [{ id, name }]
const toList = (arr) =>
  Array.isArray(arr)
    ? arr
        .map((a, idx) => (typeof a === 'string' ? { id: String(idx), name: a } : { id: String(a?.id ?? a?.pk ?? idx), name: a?.name ?? a?.title ?? a?.label ?? '' }))
        .filter((x) => x.name)
    : [];

const cap = (value) => (value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : '');

// ----------------------------------------------------------------------
// small presentational helpers
// ----------------------------------------------------------------------
function Donut({ value, size = 56, stroke = 6, color = '#01A8F8' }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, value)) / 100) * c;
  const mid = size / 2;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flex: `0 0 ${size}px` }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={mid} cy={mid} r={r} fill="none" stroke="#eef1f4" strokeWidth={stroke} />
        <circle cx={mid} cy={mid} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${dash.toFixed(1)} ${c.toFixed(1)}`} transform={`rotate(-90 ${mid} ${mid})`} />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, color: 'var(--app-text)' }}>{Math.round(value)}%</Box>
    </Box>
  );
}

function InfoField({ label, value, color }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--app-muted)', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: color || 'var(--app-text)' }}>{value || '—'}</Typography>
    </Box>
  );
}

function KeyValueRow({ label, value }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
      <Typography sx={{ fontSize: 13, color: 'var(--app-text-secondary)' }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'var(--app-text)', textAlign: 'right' }}>{value || '—'}</Typography>
    </Stack>
  );
}

function ChipGroup({ label, values }) {
  const hasValues = Array.isArray(values) && values.length > 0;
  const list = hasValues ? values : ['Not set'];
  return (
    <Box>
      <Typography sx={{ fontSize: 11, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--app-muted)', mb: 1 }}>{label}</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {list.map((value, i) => {
          const brand = hasValues && i === 0;
          return (
            <Box
              key={`${value}-${i}`}
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                px: 1.6,
                py: 0.6,
                borderRadius: 999,
                bgcolor: !hasValues ? '#f5f6f8' : brand ? '#E6F7FF' : 'var(--app-surface)',
                color: !hasValues ? '#9aa1ad' : brand ? '#0088C7' : 'var(--app-text-secondary)',
                border: '1px solid',
                borderColor: !hasValues || brand ? 'transparent' : 'var(--app-border)'
              }}
            >
              {value}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// ----------------------------------------------------------------------
// Edit profile fly-in
// ----------------------------------------------------------------------
function EditProfileDrawer({ open, onClose, initial, onSave, saving }) {
  const [form, setForm] = useState(initial);
  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const fields = [
    { key: 'firstName', label: 'First name' },
    { key: 'lastName', label: 'Last name' },
    { key: 'username', label: 'Username' },
    { key: 'phone', label: 'Phone' },
    { key: 'timezone', label: 'Timezone' },
    { key: 'language', label: 'Language' }
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, bgcolor: 'var(--app-surface)', backgroundImage: 'none' } }}>
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ p: 3, borderBottom: '1px solid var(--app-divider)' }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--app-muted)' }}>Profile</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 0.5 }}>Edit profile</Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close"><CloseOutlined /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.75 }}>
            {fields.map(({ key, label }) => (
              <TextField key={key} label={label} value={form[key] || ''} onChange={set(key)} fullWidth size="small" />
            ))}
            <TextField
              label="Birthdate"
              type="date"
              value={form.birth_date || ''}
              onChange={set('birth_date')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              value={form.country || null}
              onChange={(_, next) => setForm((prev) => ({ ...prev, country: next || '' }))}
              options={countries.map((item) => item.label)}
              renderInput={(params) => <TextField {...params} label="Country" size="small" />}
            />
            <Box sx={{ gridColumn: 'span 2' }}>
              <TextField label="About me" value={form.aboutme || ''} onChange={set('aboutme')} fullWidth multiline minRows={4} size="small" />
            </Box>
          </Box>
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={1.25} sx={{ p: 2, borderTop: '1px solid var(--app-divider)' }}>
          <Button variant="outlined" color="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={() => onSave(form)} disabled={saving} sx={{ bgcolor: '#01A8F8', '&:hover': { bgcolor: '#0088C7' } }}>{saving ? 'Saving…' : 'Save'}</Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

// ----------------------------------------------------------------------
// Edit trader-info fly-in
// ----------------------------------------------------------------------
function EditTraderDrawer({ open, onClose, initial, lookups, onSave, saving }) {
  const [experience, setExperience] = useState(initial.experience);
  const [selected, setSelected] = useState(initial.selected);
  useEffect(() => {
    if (open) {
      setExperience(initial.experience);
      setSelected(initial.selected);
    }
  }, [open, initial]);

  const toggle = (groupKey, id) => () =>
    setSelected((prev) => {
      const current = new Set(prev[groupKey]);
      if (current.has(id)) current.delete(id);
      else current.add(id);
      return { ...prev, [groupKey]: current };
    });

  const groups = [
    { key: 'markets', label: 'Markets', catalog: lookups.markets },
    { key: 'assets', label: 'Assets', catalog: lookups.assets },
    { key: 'instruments', label: 'Instruments', catalog: lookups.instruments },
    { key: 'timeframes', label: 'Timeframes', catalog: lookups.timeframes },
    { key: 'indicators', label: 'Indicators', catalog: lookups.indicators }
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, bgcolor: 'var(--app-surface)', backgroundImage: 'none' } }}>
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ p: 3, borderBottom: '1px solid var(--app-divider)' }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--app-muted)' }}>Profile</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 0.5 }}>Edit trader info</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'var(--app-muted)', mt: 0.5 }}>Pick everything that describes your trading — Guardian matches against it.</Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close"><CloseOutlined /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--app-text-secondary)', mb: 0.75 }}>Experience</Typography>
            <TextField select value={experience || ''} onChange={(event) => setExperience(event.target.value)} fullWidth size="small">
              <MenuItem value="">Select…</MenuItem>
              {EXPERIENCE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{cap(option)}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Stack spacing={2.5}>
            {groups.map(({ key, label, catalog }) => (
              <Box key={key}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--app-text-secondary)', mb: 1 }}>{label}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {catalog.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: 'var(--app-muted)' }}>No options available.</Typography>
                  ) : (
                    catalog.map((option) => {
                      const on = selected[key].has(String(option.id));
                      return (
                        <Box
                          key={option.id}
                          component="button"
                          type="button"
                          onClick={toggle(key, String(option.id))}
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            px: 1.75,
                            py: 0.75,
                            borderRadius: 999,
                            cursor: 'pointer',
                            userSelect: 'none',
                            bgcolor: on ? '#E6F7FF' : 'var(--app-surface)',
                            color: on ? '#0088C7' : 'var(--app-text-secondary)',
                            border: '1px solid',
                            borderColor: on ? '#01A8F8' : 'var(--app-border)'
                          }}
                        >
                          {option.name}
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={1.25} sx={{ p: 2, borderTop: '1px solid var(--app-divider)' }}>
          <Button variant="outlined" color="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={() => onSave({ experience, selected })} disabled={saving} sx={{ bgcolor: '#01A8F8', '&:hover': { bgcolor: '#0088C7' } }}>{saving ? 'Saving…' : 'Save'}</Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

// ----------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------
export default function ViewProfilePage() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { makeAuthenticatedRequest } = useAuthenticatedRequest();
  const [searchParams, setSearchParams] = useSearchParams();
  const brokerLive = useIbkrBrokerLiveState();

  const tab = searchParams.get('tab') === 'trader-info' ? 'trader-info' : 'user-info';

  const [userExt, setUserExt] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userGameInfo, setUserGameInfo] = useState(null);
  const [traderProfile, setTraderProfile] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [lookups, setLookups] = useState({ assets: [], instruments: [], markets: [], timeframes: [], indicators: [] });

  const [editOpen, setEditOpen] = useState(false);
  const [traderEditOpen, setTraderEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [traderSaving, setTraderSaving] = useState(false);

  const loadAll = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    const results = await Promise.allSettled([
      makeAuthenticatedRequest('/api/v1/account/userextension/me/'),
      makeAuthenticatedRequest('/api/v1/account/userprofile/me/'),
      makeAuthenticatedRequest('/api/v1/account/usergameinfo/me/'),
      makeAuthenticatedRequest('/api/v1/account/traderprofile/me/'),
      makeAuthenticatedRequest('/api/v1/journal/strategies/'),
      makeAuthenticatedRequest('/api/v1/account/traderprofile/lookups/')
    ]);
    if (results[0].status === 'fulfilled') setUserExt(results[0].value || {});
    if (results[1].status === 'fulfilled') setUserProfile(results[1].value || {});
    if (results[2].status === 'fulfilled') setUserGameInfo(results[2].value || {});
    if (results[3].status === 'fulfilled') setTraderProfile(results[3].value || {});
    if (results[4].status === 'fulfilled') setStrategies(toList(results[4].value));
    if (results[5].status === 'fulfilled') {
      const lk = results[5].value || {};
      setLookups({
        assets: toList(lk.assets),
        instruments: toList(lk.instruments),
        markets: toList(lk.markets),
        timeframes: toList(lk.timeframes),
        indicators: toList(lk.indicators)
      });
    }
  }, [isLoaded, isSignedIn, makeAuthenticatedRequest]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ---- derived display values ----
  const meta = user?.publicMetadata || {};
  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const displayName = user?.fullName || [firstName, lastName].filter(Boolean).join(' ') || user?.username || 'Trader';
  const username = user?.username || '';
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const memberSince = user?.createdAt ? moment(user.createdAt).format('MMM D, YYYY') : '—';
  const country = userExt?.country || meta.country || '';
  const timezone = userProfile?.timezone || meta.timezone || '';
  const language = userProfile?.language || meta.language || '';
  const phone = userExt?.phone_number || meta.phone_number || '';
  const rawBirth = userExt?.birth_date || meta.birth_date || '';
  const birthDisplay = rawBirth ? (moment(rawBirth, ['YYYY-MM-DD', 'MM/DD/YYYY'], true).isValid() ? moment(rawBirth, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('MM/DD/YYYY') : rawBirth) : '';
  const aboutMe = userProfile?.aboutme || meta.aboutme || '';

  const completion = useMemo(() => {
    const values = [firstName, lastName, username, birthDisplay, country, timezone, language, phone, aboutMe];
    const filled = values.filter((v) => v !== undefined && v !== null && String(v).trim() !== '').length;
    return Math.round((filled / values.length) * 100);
  }, [firstName, lastName, username, birthDisplay, country, timezone, language, phone, aboutMe]);

  const brokerLink = useMemo(() => {
    const connection = brokerLive?.connection;
    if (connection?.connected && !connection?.auth_expired) {
      const ids = connection.account_ids?.length ? ` · ${connection.account_ids.join(', ')}` : '';
      return `IBKR${ids}`;
    }
    return 'Demo · not connected';
  }, [brokerLive]);

  const twoFactor = user?.twoFactorEnabled ? 'Enabled' : 'Disabled';

  // trader selections (normalised {id,name})
  const tradingMarkets = toList(traderProfile?.trading_markets);
  const tradingAssets = toList(traderProfile?.trading_assets);
  const tradingInstruments = toList(traderProfile?.trading_instruments);
  const tradingTimeframes = toList(traderProfile?.trading_timeframes);
  const tradingIndicators = toList(traderProfile?.trading_indicators);
  const experience = traderProfile?.trading_experience || '';

  const traderGroups = [
    { label: 'Experience', values: experience ? [cap(experience)] : [] },
    { label: 'Markets', values: tradingMarkets.map((x) => x.name) },
    { label: 'Assets & instruments', values: [...tradingAssets, ...tradingInstruments].map((x) => x.name) },
    { label: 'Timeframes', values: tradingTimeframes.map((x) => x.name) },
    { label: 'Strategies', values: strategies.map((x) => x.name) },
    { label: 'Indicators', values: tradingIndicators.map((x) => x.name) }
  ];

  // trader standing (usergameinfo — display, with design fallbacks)
  const gi = userGameInfo || {};
  const gradeCode = gi.rank_code || gi.grade || 'T-1';
  const gradeName = gi.rank_name || gi.rank || 'Ensign';
  const gradeProgress = Number.isFinite(Number(gi.rank_progress)) ? Number(gi.rank_progress) : 62;
  const standingRows = [
    { label: 'Six Star Trades', value: String(gi.six_star_trades ?? gi.star_trades ?? 127) },
    { label: 'Consistency streak', value: gi.streak != null ? `${gi.streak} days` : '10 days' },
    { label: 'Rule compliance (30d)', value: gi.rule_compliance != null ? `${gi.rule_compliance}%` : '86%' },
    { label: 'Leaderboard', value: gi.leaderboard_rank != null ? `#${gi.leaderboard_rank}` : '#475' }
  ];

  // ---- edit initial values ----
  const editInitial = useMemo(
    () => ({
      firstName,
      lastName,
      username,
      phone,
      timezone,
      language,
      country,
      aboutme: aboutMe,
      birth_date: rawBirth && moment(rawBirth, ['YYYY-MM-DD', 'MM/DD/YYYY'], true).isValid() ? moment(rawBirth, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('YYYY-MM-DD') : ''
    }),
    [firstName, lastName, username, phone, timezone, language, country, aboutMe, rawBirth]
  );

  const traderInitial = useMemo(
    () => ({
      experience,
      selected: {
        markets: new Set(tradingMarkets.map((x) => String(x.id))),
        assets: new Set(tradingAssets.map((x) => String(x.id))),
        instruments: new Set(tradingInstruments.map((x) => String(x.id))),
        timeframes: new Set(tradingTimeframes.map((x) => String(x.id))),
        indicators: new Set(tradingIndicators.map((x) => String(x.id)))
      }
    }),
    [traderProfile]
  );

  // ---- save handlers ----
  const handleSaveProfile = async (form) => {
    setSaving(true);
    try {
      const birthIso = form.birth_date ? moment(form.birth_date, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('YYYY-MM-DD') : null;
      const targets = [
        ['/api/v1/account/users/me/', { first_name: (form.firstName || '').trim(), last_name: (form.lastName || '').trim(), username: (form.username || '').trim() || null }],
        ['/api/v1/account/userextension/me/', { country: form.country || null, birth_date: birthIso, phone_number: (form.phone || '').trim() || null }],
        ['/api/v1/account/userprofile/me/', { timezone: (form.timezone || '').trim() || null, language: (form.language || '').trim() || null, aboutme: form.aboutme || '' }]
      ];
      const results = await Promise.allSettled(targets.map(([endpoint, data]) => makeAuthenticatedRequest(endpoint, { method: 'PATCH', data, skipGlobalErrorHandler: true })));
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length) {
        throw new Error(failures.map((f) => f.reason?.message || 'Unknown error').join(' | '));
      }
      await Promise.allSettled([user?.reload?.(), loadAll()]);
      openSnackbar({ open: true, message: 'Profile updated!', variant: 'alert' });
      setEditOpen(false);
    } catch (err) {
      openSnackbar({ open: true, message: `Profile save failed: ${err?.message || 'Unknown error'}`, variant: 'alert', alert: { color: 'error' } });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTrader = async ({ experience: nextExperience, selected }) => {
    setTraderSaving(true);
    try {
      const ids = (set) => Array.from(set).map(Number).filter(Number.isFinite);
      const data = {
        trading_experience: nextExperience || null,
        trading_market_ids: ids(selected.markets),
        trading_asset_ids: ids(selected.assets),
        trading_instrument_ids: ids(selected.instruments),
        trading_timeframe_ids: ids(selected.timeframes),
        trading_indicator_ids: ids(selected.indicators)
      };
      await makeAuthenticatedRequest('/api/v1/account/traderprofile/me/', { method: 'PATCH', data, skipGlobalErrorHandler: true });
      await loadAll();
      openSnackbar({ open: true, message: 'Trader info updated!', variant: 'alert' });
      setTraderEditOpen(false);
    } catch (err) {
      openSnackbar({ open: true, message: `Trader info save failed: ${err?.message || 'Unknown error'}`, variant: 'alert', alert: { color: 'error' } });
    } finally {
      setTraderSaving(false);
    }
  };

  const openContextualEdit = () => (tab === 'trader-info' ? setTraderEditOpen(true) : setEditOpen(true));

  const personalFields = [
    { label: 'First name', value: firstName },
    { label: 'Last name', value: lastName },
    { label: 'Username', value: username },
    { label: 'Birthdate', value: birthDisplay },
    { label: 'Country', value: country },
    { label: 'Timezone', value: timezone },
    { label: 'Language', value: language },
    { label: 'Phone', value: phone }
  ];

  return (
    <RedesignShell>
      <Box sx={{ px: { xs: 2.5, md: '44px' }, py: { xs: 3, md: '34px' }, minHeight: '100%' }}>
        {/* header */}
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: '-.02em' }}>Profile</Typography>
            <Typography sx={{ mt: 0.6, fontSize: 14, color: 'var(--app-text-secondary)' }}>Who you are — and how you trade.</Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
            <BrokerConnectionStatus />
            <HeaderUtilities />
            <Profile compact />
          </Stack>
        </Stack>
        <Divider sx={{ my: 2.75, borderColor: 'var(--app-divider)' }} />

        {/* identity card */}
        <Box sx={{ ...cardSx, display: 'flex', alignItems: 'center', gap: 2.75, flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative', width: 84, height: 84, flex: '0 0 84px' }}>
            <Box
              component={user?.imageUrl ? 'img' : 'span'}
              src={user?.imageUrl || undefined}
              alt={displayName}
              sx={{ position: 'absolute', inset: 0, width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', background: AVATAR_GRADIENT }}
            />
            <IconButton
              onClick={() => setEditOpen(true)}
              aria-label="Change photo"
              sx={{ position: 'absolute', right: -2, bottom: -2, width: 28, height: 28, borderRadius: '50%', border: '2px solid var(--app-surface)', bgcolor: '#01A8F8', color: '#fff', '&:hover': { bgcolor: '#0088C7' } }}
            >
              <FormOutlined style={{ fontSize: 13 }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.01em' }}>{displayName}</Typography>
              {username ? <Box sx={{ fontSize: 12, fontWeight: 600, color: '#0088C7', bgcolor: '#E6F7FF', px: 1.25, py: 0.4, borderRadius: 999 }}>@{username}</Box> : null}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.9, fontSize: 13, color: 'var(--app-muted)' }}>
              {email ? <span>{email}</span> : null}
              {email ? <span>·</span> : null}
              <span>Member since {memberSince}</span>
              {country ? <span>·</span> : null}
              {country ? <span>{country}</span> : null}
            </Stack>
          </Box>

          <Stack direction="row" alignItems="center" spacing={2.25} flexWrap="wrap" useFlexGap>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Donut value={completion} />
              <Typography sx={{ fontSize: 12.5, color: 'var(--app-muted)', maxWidth: 120, lineHeight: 1.4 }}>Profile completeness</Typography>
            </Stack>
            <Button onClick={openContextualEdit} startIcon={<EditOutlined />} variant="contained" sx={{ height: 40, borderRadius: '10px', bgcolor: '#01A8F8', '&:hover': { bgcolor: '#0088C7' } }}>
              Edit profile
            </Button>
          </Stack>
        </Box>

        {/* tabs */}
        <Tabs
          value={tab}
          onChange={(_, next) => setSearchParams({ tab: next })}
          sx={{ width: 'fit-content', minHeight: 40, bgcolor: 'var(--app-surface-subtle)', borderRadius: '11px', p: 0.4, my: 2.75, '& .MuiTab-root': { minHeight: 36, py: 0, px: 2.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: 'var(--app-text-secondary)' }, '& .Mui-selected': { bgcolor: 'var(--app-surface)', color: '#0088C7' }, '& .MuiTabs-indicator': { display: 'none' } }}
        >
          <Tab label="User Info" value="user-info" />
          <Tab label="Trader Info" value="trader-info" />
        </Tabs>

        {/* USER INFO */}
        {tab === 'user-info' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(280px, 1fr)' }, gap: 2, alignItems: 'start' }}>
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2.25 }}>Personal</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '18px 24px' }}>
                {personalFields.map((field) => (
                  <InfoField key={field.label} label={field.label} value={field.value} />
                ))}
              </Box>
              <Divider sx={{ my: 2.5, borderColor: 'var(--app-divider)' }} />
              <Typography sx={{ fontSize: 11, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--app-muted)', mb: 0.9 }}>About me</Typography>
              <Typography sx={{ fontSize: 14, color: 'var(--app-text-secondary)', lineHeight: 1.6 }}>{aboutMe || 'Add a short bio so Guardian and the community know how you trade.'}</Typography>
            </Box>

            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2.25 }}>Account</Typography>
              <Stack spacing={2}>
                <KeyValueRow label="Email" value={email} />
                <KeyValueRow label="Member since" value={memberSince} />
                <KeyValueRow label="Broker link" value={brokerLink} />
                <KeyValueRow label="Two-factor auth" value={twoFactor} />
              </Stack>
              <Divider sx={{ my: 2.25, borderColor: 'var(--app-divider)' }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--app-text)' }}>Plan</Typography>
                  <Typography sx={{ fontSize: 12, color: 'var(--app-muted)', mt: 0.25 }}>Free · upgrade for AI insights</Typography>
                </Box>
                <Button sx={{ height: 34, px: 1.75, borderRadius: '9px', bgcolor: '#FFCE6F', color: '#252F40', fontWeight: 700, '&:hover': { bgcolor: '#f2bd4f' } }}>Upgrade</Button>
              </Stack>
            </Box>
          </Box>
        ) : (
          /* TRADER INFO */
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(280px, 1fr)' }, gap: 2, alignItems: 'start' }}>
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>How I trade</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'var(--app-muted)', mb: 2.25 }}>Guardian uses this to match cycles against your declared style.</Typography>
              <Stack spacing={2.25}>
                {traderGroups.map((group) => (
                  <ChipGroup key={group.label} label={group.label} values={group.values} />
                ))}
              </Stack>
            </Box>

            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2.25 }}>Trader standing</Typography>
              <Stack direction="row" alignItems="center" spacing={1.75}>
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: '#E6F7FF', color: '#0088C7', display: 'grid', placeItems: 'center' }}>
                  <TrophyOutlined style={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{gradeCode} · {gradeName}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'var(--app-muted)' }}>{gradeProgress}% to next grade</Typography>
                </Box>
              </Stack>
              <Box sx={{ height: 8, borderRadius: 999, bgcolor: '#eef1f4', my: 2, overflow: 'hidden' }}>
                <Box sx={{ width: `${Math.max(0, Math.min(100, gradeProgress))}%`, height: '100%', bgcolor: '#01A8F8', borderRadius: 999 }} />
              </Box>
              <Stack spacing={1.75}>
                {standingRows.map((row) => (
                  <KeyValueRow key={row.label} label={row.label} value={row.value} />
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>

      <EditProfileDrawer open={editOpen} onClose={() => setEditOpen(false)} initial={editInitial} onSave={handleSaveProfile} saving={saving} />
      <EditTraderDrawer open={traderEditOpen} onClose={() => setTraderEditOpen(false)} initial={traderInitial} lookups={lookups} onSave={handleSaveTrader} saving={traderSaving} />
    </RedesignShell>
  );
}
